/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
"use client";

import { useState } from "react";
import { Card, CardContent, CardHeader } from "../ui/card";
import { cn, currencyHelper, dateFormater, formatRupee } from "@/lib/utils";
import {
  Calendar,
  Check,
  Clock,
  Loader2,
  MapPin,
  MinusCircle,
  PlusCircle,
  User2,
} from "lucide-react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { PhoneInput } from "../ui/phone-input";
import { api, type RouterOutputs } from "@/trpc/react";
import { type AddonType } from "@/types/trip.type";
import { type UserType } from "@/types/user.type";
import { toast } from "sonner";
import DateGroupSliderBooking from "../global/group-date-booking";
import { Coupen } from "../icons/coupen";
import { DESTINATION_BG } from "@/lib/constant";
import { ImageLoader } from "../ui/image-loader";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";

interface BookingFormProps {
  data: RouterOutputs["trips"]["get_for_booking"];
  tripId: string;
  img: string | null;
  user: UserType;
}

export default function BookingForm({
  data,
  tripId,
  img,
  user,
}: BookingFormProps) {
  const {
    addons,
    enabledPartialPay,
    number_of_days,
    number_of_nights,
    title,
    trip_batches,
    adentureType,
  } = data!;
  const ParsedAddons: AddonType[] = JSON.parse(
    JSON.stringify(addons),
  ) as AddonType[];
  const router = useRouter();
  // Coupon State variables
  const [discount, setDiscount] = useState<{
    discount_amount: number;
    couponId: string;
  } | null>();



  const [clickOnPartialed,setClickOnPartialed] = useState(false)

  // useEffect( ()=>{setClickOnPartialed(false)},[] )

  const [couponCode, setCouponCode] = useState<string>("");
  const { mutateAsync: fetchCoupon, isPending: couponLoading } =
    api.coupon.fetchCoupon.useMutation({
      onSuccess: (data) => {
        toast.success("Coupon applied successfully.");
        setDiscount(data); // Use the returned data, not Coupon
      },
      onError: (error) => {
        toast.error(
          error.message ?? "An error occurred while fetching coupon.",
        );
      },
    });
  const { mutateAsync: MutateCheckout, isPending: checkoutLoading } =
    api.booking.checkout.useMutation({
      onError: (error) => {
        toast.error(error.message ?? "An error occurred while checking out.");
      },
    });
  const createBookingMutation = api.booking.createBookingWithoutPayment.useMutation({
    onSuccess: () => {
      toast.success("Booking request saved. Our team will follow up.");
      router.push("/user-dashboard/bookings");
    },
    onError: (error) => {
      toast.error(error.message ?? "Failed to create booking");
    },
  });
  const { mutateAsync: MutateVerify, isPending: verifyLoading } =
    api.booking.verify.useMutation({
      onSuccess: () => {
        toast.success(
          "Booking completed successfully. A Booking confirmation email has been sent to your email.",
        );
        router.push(`/user-dashboard/bookings`);
      },
      onError: (error) => {
        toast.error(
          error.message ?? "An error occurred while verifying payment.",
        );
      },
    });
  const [phone, setPhone] = useState<string | null | undefined>(null);

  // State variables
  const [selectedCity, setSelectedCity] = useState<string>(
    trip_batches[0]?.city ?? "",
  );

  const [selectedLocation, setSelectedLocation] = useState<string | undefined>(
    undefined,
  );

  const [selectedDate, setSelectedDate] = useState<{
    id: string;
    start_date: Date;
    end_date: Date;
  } | null>(null);
  const [selectedDatePrice, setSelectedDatePrice] = useState<number | null>(
    null,
  );
  const [selectedTransportOptions, setSelectedTransportOptions] = useState<
    {
      id: string;
      transport: string;
      price_per_person: number;
      people: number;
      max_capacity: number;
    }[]
  >([]);
  const [selectedAddons, setSelectedAddons] = useState<Record<string, number>>(
    {},
  );
  const [isChecked, setIsChecked] = useState(false);

  // Handle city change
  const handleCityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newCity = event.target.value;
    setSelectedCity(newCity);
    setSelectedDate(null);
    setSelectedDatePrice(null);
    setSelectedLocation(undefined);
    setSelectedTransportOptions(
      trip_batches
        .find((batch) => batch.city === newCity)
        ?.available_dates.find((d) => d.id === selectedDate?.id)
        ?.mode_of_transports?.map((mode) => ({
          id: mode.id,
          transport: mode.transport,
          price_per_person: mode.price_per_person,
          people: 0,
          max_capacity: mode.max_capacity,
        })) ?? [],
    );
    setSelectedAddons({});
  };

  // Handle date change
  const handleDateChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    dateData: { id: string; start_date: Date; end_date: Date; price: number },
  ) => {
    setSelectedDate({
      id: dateData.id,
      start_date: dateData.start_date,
      end_date: dateData.end_date,
    });
    setSelectedDatePrice(dateData.price);
  };

  // Update people count for transport options
  const handleTransportPeopleChange = (
    action: "increment" | "decrement",
    transport: {
      id: string;
      transport: string;
      price_per_person: number;
      max_capacity: number;
    },
  ) => {
    setSelectedTransportOptions((prevOptions) => {
      const existingOption = prevOptions.find(
        (option) => option.id === transport.id,
      );

      // Determine new people count
      let newPeopleCount = existingOption?.people ?? 0;
      if (action === "increment") {
        newPeopleCount++;
      } else if (action === "decrement" && newPeopleCount > 0) {
        newPeopleCount--;
      }

      // Ensure count is within valid range
      if (newPeopleCount < 0 || newPeopleCount > transport.max_capacity) {
        return prevOptions;
      }

      // Update or add the transport option
      if (existingOption) {
        return prevOptions.map((option) =>
          option.id === transport.id
            ? { ...option, people: newPeopleCount }
            : option,
        );
      } else {
        return [...prevOptions, { ...transport, people: newPeopleCount }];
      }
    });
  };

  // Update count for add-ons
  const handleAddonChange = (
    action: "increment" | "decrement",
    addon: AddonType,
  ) => {
    setSelectedAddons((prevAddons) => {
      const currentCount = prevAddons[addon.title] ?? 0;
      if (action === "increment") {
        return { ...prevAddons, [addon.title]: currentCount + 1 };
      } else if (currentCount > 0) {
        return { ...prevAddons, [addon.title]: currentCount - 1 };
      }
      return prevAddons;
    });
  };

  // Get available dates for the selected city
  const getAvailableDates = () => {
    const selectedBatch = trip_batches.find(
      (batch) => batch.city === selectedCity,
    );
    return selectedBatch ? selectedBatch.available_dates : [];
  };

  const availableDates = getAvailableDates();

  // Calculate the total number of people from transport options
  const totalPeopleForTransport = selectedTransportOptions.reduce(
    (sum, option) => sum + option.people,
    0,
  );

  // Calculate the total price
  const transportCost = selectedTransportOptions.reduce(
    (sum, option) => sum + option.price_per_person * option.people,
    0,
  );
  const dateCost = selectedDatePrice
    ? selectedDatePrice * totalPeopleForTransport
    : 0;
  const addonCost = Object.keys(selectedAddons).reduce((sum, key) => {
    const addon: AddonType | undefined = Array.isArray(addons)
      ? (addons as AddonType[]).find((a: AddonType) => a.title === key)
      : undefined;
    return (
      sum + (addon ? parseInt(addon.price) * (selectedAddons[key] ?? 0) : 0)
    );
  }, 0);

  const totalPrice = transportCost + dateCost + addonCost;
  const subtotalAfterDiscount = totalPrice - (discount?.discount_amount ?? 0);
  const gst_amount = Number((subtotalAfterDiscount * 0.05).toFixed(2));
  const grandTotal = Number((subtotalAfterDiscount + gst_amount).toFixed(2));

  const quoteTransport = selectedTransportOptions
    .filter((option) => option.people > 0)
    .map((option) => ({
      transport: option.transport,
      numberOfTravelers: option.people,
    }));
  const quoteAddons = Object.entries(selectedAddons)
    .filter(([, count]) => count > 0)
    .map(([addonTitle, count]) => ({
      addon_title: addonTitle,
      addon_unit: count,
    }));
  const canQuote = Boolean(
    selectedDate &&
    selectedCity &&
    totalPeopleForTransport > 0 &&
    quoteTransport.length > 0,
  );
  const { data: paymentQuote } = api.booking.paymentQuote.useQuery(
    {
      tripId,
      adventureType: adentureType,
      city: selectedCity,
      startDate: selectedDate?.start_date ?? new Date(0),
      endDate: selectedDate?.end_date ?? new Date(0),
      totalMembers: Math.max(totalPeopleForTransport, 1),
      transportDetails: quoteTransport,
      addonDetails: quoteAddons,
      discountAmount: discount?.discount_amount ?? 0,
    },
    { enabled: canQuote },
  );
  const paymentEnabled = trip_batches
    .find((batch) => batch.city === selectedCity)
    ?.available_dates.find((date) => date.id === selectedDate?.id)
    ?.enabled_booking !== false;
  const partialAvailable = paymentEnabled && (paymentQuote?.partialAvailable ?? false);
  const amountDueNow = partialAvailable ? paymentQuote?.payNow ?? 0 : paymentQuote?.grandTotal ?? grandTotal;
  const amountDueLater = partialAvailable ? paymentQuote?.payLater ?? 0 : 0;
  
  const handleEnquiryBooking = () => {
    if (totalPeopleForTransport <= 0) {
      toast.error("Please select at least one person for booking.");
      return;
    }
    if (!phone && !user?.phone) {
      toast.error("Phone is required to make booking request.");
      return;
    }
    if (!selectedDate || !selectedDatePrice) {
      toast.error("Please select a date for your booking.");
      return;
    }

    const selectedDateData = trip_batches
      .find((batch) => batch.city === selectedCity)
      ?.available_dates.find((date) => date.id === selectedDate.id);
    const dateAddons = selectedDateData?.addons;
    const addonSource = dateAddons && Array.isArray(dateAddons) && dateAddons.length > 0
      ? (dateAddons as AddonType[])
      : ParsedAddons;

    const addonDetails = Object.entries(selectedAddons)
      .filter(([, count]) => count > 0)
      .map(([addonTitle, count]) => ({
        addon_title: addonTitle,
        addon_price: addonSource.find((addon) => addon.title === addonTitle)?.price ?? "0",
        addon_unit: count,
      }));

    createBookingMutation.mutate({
      tripId,
      tripTitle: title,
      adventureType: adentureType,
      city: selectedCity,
      pickupLocation: selectedLocation,
      startDate: selectedDate.start_date,
      endDate: selectedDate.end_date,
      totalMembers: totalPeopleForTransport,
      transportDetails: selectedTransportOptions
        .filter((option) => option.people > 0)
        .map((option) => ({
          transport: option.transport,
          numberOfTravelers: option.people,
          pricePerPerson: selectedDatePrice + option.price_per_person,
          totalPrice: (selectedDatePrice + option.price_per_person) * option.people,
        })),
      addonDetails,
      pricing: {
        basePrice: dateCost,
        transportCost,
        addonCost,
        subtotal: totalPrice,
        gst: gst_amount,
        grandTotal,
        enabledPartialPay,
        partialAmount: null,
        remainingAmount: null,
      },
      phone: (phone ?? user.phone)!,
      couponId: discount?.couponId,
      discountAmount: discount?.discount_amount,
    });
  };

  const handleCouponFetch = async () => {
    await fetchCoupon({
      code: couponCode,
      productId: tripId,
      totalAmount: totalPrice,
      userId: user?.id,
    });
  };

  // Handle booking submission
  const handleBooking = async () => {
    if (totalPeopleForTransport > 0) {
      if (!phone && !user?.phone) {
        toast.error("Phone is required to make booking request. ");
        return;
      }

      const addonDetails = Object.entries(selectedAddons)
        .filter(([, count]) => count > 0)
        .map(([addonTitle, count]) => ({
          addon_title: addonTitle,
          addon_price: ParsedAddons.find((a) => a.title === addonTitle)?.price,
          addon_unit: count,
        }));

      // Convert the array of addon details to JSON without pretty-printing
      const addonDetailsJSON = JSON.stringify(addonDetails);

      const order_res = await MutateCheckout({
        amount: clickOnPartialed ? amountDueNow : (paymentQuote?.grandTotal ?? grandTotal),
        phone: (phone ?? user?.phone)!, // Ensures TypeScript understands it's not undefined
        userid: user.id,
      });

      if (
        order_res.order &&
        selectedDate?.start_date &&
        selectedDate.end_date &&
        user.id &&
        selectedDatePrice
      ) {
        const options = {
          key: process.env.NEXT_PUBLIC_RAZORPAY_API_KEY, // Enter the Key id generated from the Dashboard
          amount: order_res.order.amount, // Amount is in currency subunits. Default currency is INR. Hence, 50000 refers to 50000 paise
          currency: "INR",
          name: "Trek Karo India",
          description: "Trip payment for Trek Karo India",
          image: "https://cdn-icons-png.flaticon.com/512/219/219970.png",
          order_id: order_res.order.id, // This is the Order ID obtained in the response of Step 1
          handler: async function (response: {
            razorpay_payment_id: string;
            razorpay_order_id: string;
            razorpay_signature: string;
          }) {
            await MutateVerify({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              city: selectedCity,
              start_date: selectedDate?.start_date,
              end_date: selectedDate?.end_date,
              total_amount: totalPrice,
              total_members: totalPeopleForTransport,
              transport_details: selectedTransportOptions.map((options) => ({
                number_of_travelors: options.people,
                transport_optionns: options.transport,
                transport_price: selectedDatePrice + options.price_per_person,
              })),
              tripId,
              userId: user?.id,
              addon_details: addonDetailsJSON,
              gst: paymentQuote?.gst ?? gst_amount,
              recieved_amount: clickOnPartialed ? amountDueNow : (paymentQuote?.grandTotal ?? grandTotal),
              remaining_amount: clickOnPartialed ? amountDueLater : 0 ,
              grand_total: paymentQuote?.grandTotal ?? grandTotal,
              couponId: discount?.couponId,
              discount_amount: discount?.discount_amount,
              trip_title: title,
              pickup_location: selectedLocation,
              adventureType: adentureType,
            });
          },
          prefill: {
            name: user.fullname,
            email: user.email,
            phone: user.phone ?? phone,
          },
          notes: {
            address: "Trek Karo India Pvt. Ltd.",
          },
          theme: {
            color: "#3399cc",
          },
        };

        // Initialize Razorpay if `window` is defined (i.e., in the browser)

        // @ts-expect-error i know razorpay exists
        const razor = new window.Razorpay(options); // Access Razorpay from the window object
        razor.open(); // Trigger Razorpay's payment modal

        // Add event listener for payment failures
        razor.on(
          "payment.failed",
          function (response: {
            error: {
              code: unknown;
              description: string;
              source: unknown;
              step: unknown;
              reason: string;
              metadata: { order_id: unknown; payment_id: unknown };
            };
          }) {
            toast.error(response.error.description);
          },
        );
      }
    } else {
      toast.error("Please select at least one person for booking.");
    }
  };

  const [currentForm, setCurrentForm] = useState<number>(0);

  const handleCheckboxChange = () => {
    setIsChecked((prev) => !prev); // Toggles the state
  };

  return (
    <>
      <div className="grid grid-cols-4 gap-3 py-6 text-sm leading-tight md:text-base">
        <div className="flex flex-col gap-1">
          <p>Select Batch</p>
          <span className="relative h-2.5 w-full bg-zinc-200">
            <span
              className={cn(
                "absolute left-0 top-0 h-full w-full origin-left bg-custom-secondary transition delay-75 duration-500",
                currentForm >= 0 ? "scale-x-100" : "scale-x-0",
              )}
            />
          </span>
        </div>
        <div className="flex flex-col gap-1">
          <p>No. Of Members</p>
          <span className="relative h-2.5 w-full bg-zinc-200">
            <span
              className={cn(
                "absolute left-0 top-0 h-full w-full origin-left scale-x-0 bg-custom-secondary transition delay-75 duration-500",
                currentForm >= 1 ? "scale-x-100" : "scale-x-0",
              )}
            />
          </span>
        </div>
        <div className="flex flex-col gap-1">
          <p>Payment Details</p>
          <span className="relative h-2.5 w-full bg-zinc-200">
            <span
              className={cn(
                "absolute left-0 top-0 h-full w-full origin-left scale-x-0 bg-custom-secondary transition delay-75 duration-500",
                currentForm >= 2 ? "scale-x-100" : "scale-x-0",
              )}
            />
          </span>
        </div>
        <div className="flex flex-col gap-1">
          <p>Booking Status</p>
          <span className="relative h-2.5 w-full bg-zinc-200">
            <span
              className={cn(
                "absolute left-0 top-0 h-full w-full origin-left scale-x-0 bg-custom-secondary transition delay-75 duration-500",
                currentForm >= 3 ? "scale-x-100" : "scale-x-0",
              )}
            />
          </span>
        </div>
      </div>
      <Card className="w-full overflow-hidden border-zinc-400">
        <CardHeader className="border-b border-b-zinc-400 bg-custom-primary-shade p-3 text-center font-lexend text-lg font-bold">
          <div
            className="flex h-full w-full flex-row flex-nowrap transition delay-75 duration-500"
            style={{ transform: `translateX(-${currentForm * 100}%)` }}
          >
            <p className="w-full shrink-0 text-center">
              Select Boarding City and Date
            </p>
            <p className="w-full shrink-0 text-center">
              Select Number Of Members
            </p>
            <p className="w-full shrink-0 text-center">Payment Details</p>
            <p className="w-full shrink-0 text-center">
              Booking Completed Sucessfully
            </p>
          </div>
        </CardHeader>
        <CardContent className="w-full overflow-hidden p-0">
          <div
            className="flex h-full w-full flex-row flex-nowrap transition delay-75 duration-500"
            style={{ transform: `translateX(-${currentForm * 100}%)` }}
          >
            <div className="w-full shrink-0 space-y-6 p-6">
              {/* City Selection */}
              <div>
                <h3 className="mb-2 font-bold">Select a City:</h3>
                <div className="flex flex-row flex-wrap gap-2.5">
                  {trip_batches.map((batch) => (
                    <div key={batch.city} className="flex w-fit">
                      <input
                        type="radio"
                        id={batch.city}
                        value={batch.city}
                        checked={selectedCity === batch.city}
                        onChange={handleCityChange}
                        className="sr-only mr-2"
                      />
                      <label
                        htmlFor={batch.city}
                        className="flex w-fit cursor-pointer items-center rounded border border-zinc-300 bg-custom-primary-shade px-2.5 py-1"
                      >
                        <div
                          className={cn(
                            "mr-2 h-4 w-4 rounded-full",
                            selectedCity === batch.city
                              ? "bg-green-400"
                              : "bg-white",
                          )}
                        >
                          {selectedCity === batch.city && (
                            <Check className="h-full w-full scale-[0.88] text-white" />
                          )}
                        </div>
                        <p>{batch.city}</p>
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="mb-2 font-bold">Select Pickup Location:</h3>
                <div className="flex flex-row flex-wrap gap-2.5">
                  {trip_batches.find((b) => b.city === selectedCity)
                    ?.pickup_location ? (
                    trip_batches
                      .find((b) => b.city === selectedCity)
                      ?.pickup_location?.split(", ")
                      .map((location) => (
                        <div key={location} className="flex w-fit">
                          <input
                            type="radio"
                            id={location}
                            value={location}
                            checked={selectedLocation === location}
                            onChange={(e) =>
                              setSelectedLocation(e.target.value)
                            }
                            className="sr-only mr-2"
                          />
                          <label
                            htmlFor={location}
                            className="flex w-fit cursor-pointer items-center rounded border border-zinc-300 bg-custom-primary-shade px-2.5 py-1"
                          >
                            <div
                              className={cn(
                                "mr-2 h-4 w-4 rounded-full",
                                selectedLocation === location
                                  ? "bg-green-400"
                                  : "bg-white",
                              )}
                            >
                              {selectedLocation === location && (
                                <Check className="h-full w-full scale-[0.88] text-white" />
                              )}
                            </div>
                            <p>{location}</p>
                          </label>
                        </div>
                      ))
                  ) : (
                    <p>No Pickup location available.</p>
                  )}
                </div>
              </div>

              {/* Date Selection */}
              <div className="w-full shrink-0">
                <h3 className="mb-2 font-bold">
                  Available Dates for {selectedCity}:
                </h3>
                <DateGroupSliderBooking
                  availableDates={availableDates}
                  selectedDate={selectedDate}
                  setSelectedDate={setSelectedDate}
                  handleDateChange={handleDateChange}
                />
              </div>
              <div className="flex justify-end">
                <button
                  disabled={
                    selectedDate === null || selectedLocation === undefined
                  }
                  onClick={() => setCurrentForm(currentForm + 1)}
                  className="primary-btn bg-custom-primary disabled:cursor-not-allowed disabled:opacity-55"
                >
                  Next Step
                </button>
              </div>
            </div>

            {/* Transport Options */}
            <div className="w-full shrink-0 space-y-6 p-6 md:p-8 lg:p-10 lg:pb-4">
              <div className="flex flex-col flex-nowrap gap-6 md:flex-row">
                <div className="relative h-28 w-40">
                  <ImageLoader
                    src={img ?? DESTINATION_BG}
                    alt="Trek Karo India"
                    fill
                    className="rounded-lg object-cover"
                  />
                </div>
                <div className="space-y-4">
                  <h3 className="font-lexend text-2xl font-semibold text-primary-text md:text-3xl">
                    {title}
                  </h3>
                  <div className="flex flex-col divide-y md:flex-row md:divide-x md:divide-y-0">
                    <div className="flex md:flex-col">
                      <div className="mb-1 flex flex-nowrap items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        {selectedCity} to {selectedCity}
                      </div>
                      <div className="flex flex-nowrap items-center gap-2">
                        <Clock className="h-4 w-4" />
                        {number_of_days} Days / {number_of_nights} Nights
                      </div>
                    </div>
                    <div className="mt-1 flex flex-nowrap items-center gap-2 pt-1 md:ml-6 md:mt-0 md:pl-6 md:pt-0">
                      <Calendar className="h-4 w-4" />
                      {selectedDate?.start_date.toDateString()}{" "}
                      <br className="hidden md:block" />
                      To {selectedDate?.end_date.toDateString()}
                    </div>
                  </div>
                </div>
              </div>
              <div className="relative overflow-hidden rounded-lg border border-secondary-text/40">
                <div className="grid w-full grid-cols-3 bg-custom-primary-shade px-4 py-2 text-sm md:px-8 md:text-base">
                  <p>Transport Options</p>
                  <p className="text-center">Price(per person)</p>
                  <div className="flex items-center justify-end">
                    <User2 className="size-5 md:size-6" />
                  </div>
                </div>
                <div className="space-y-5 p-3 text-sm md:p-4 md:text-base">
                  {(() => {
                    const batch = trip_batches.find(
                      (batch) => batch.city === selectedCity,
                    );
                    if (!batch) {
                      return (
                        <p>
                          No transport options available from {selectedCity}.
                        </p>
                      );
                    }

                    const selectedDateData = batch.available_dates.find(
                      (d) => d.id === selectedDate?.id,
                    );
                    if (
                      !selectedDateData ||
                      selectedDateData.mode_of_transports.length === 0
                    ) {
                      return (
                        <p>
                          No transport options available for the selected date.
                        </p>
                      );
                    }

                    return selectedDateData.mode_of_transports.map(
                      (option, index) => {
                        const selectedOption = selectedTransportOptions.find(
                          (op) => op.id === option.id,
                        );

                        return (
                          <div
                            key={index}
                            className="grid grid-cols-3 rounded-md border border-secondary-text/30 px-3 py-1.5"
                          >
                            <span>{option.transport}</span>
                            <span className="text-center">
                              {selectedDatePrice
                                ? `₹${currencyHelper(selectedDatePrice + option.price_per_person)}/-`
                                : "Please select a date"}
                            </span>
                            <div className="flex items-center justify-end">
                              <button
                                onClick={() =>
                                  handleTransportPeopleChange(
                                    "decrement",
                                    option,
                                  )
                                }
                                className="text-zinc-400"
                              >
                                <MinusCircle
                                  strokeWidth={1}
                                  className="size-4 md:size-5"
                                />
                              </button>
                              <span className="mx-2">
                                {selectedOption?.people ?? 0}
                              </span>
                              <button
                                onClick={() =>
                                  handleTransportPeopleChange(
                                    "increment",
                                    option,
                                  )
                                }
                                className="text-zinc-400"
                              >
                                <PlusCircle
                                  strokeWidth={1}
                                  className="size-4 md:size-5"
                                />
                              </button>
                            </div>
                          </div>
                        );
                      },
                    );
                  })()}
                </div>
              </div>

              {/* Add-ons */}
              <div className="relative overflow-hidden rounded-lg border border-secondary-text/40">
                <div className="grid w-full grid-cols-3 bg-custom-primary-shade px-4 py-2 text-sm md:px-8 md:text-base">
                  <p>Add On Options</p>
                  <p className="text-center">Price(per person)</p>
                  <div className="flex justify-end">
                    <User2 className="size-5 md:size-6" />
                  </div>
                </div>
                <div className="space-y-5 p-3 text-sm md:p-4 md:text-base">
                  {ParsedAddons.length > 0 ? (
                    ParsedAddons.map((addon, index) => (
                      <div
                        key={index}
                        className="grid grid-cols-3 rounded-md border border-secondary-text/30 px-3 py-1.5"
                      >
                        <span>{addon.title}</span>
                        <span className="text-center">
                          ₹{currencyHelper(parseInt(addon.price))}/-
                        </span>
                        <div className="flex items-center justify-end">
                          <button
                            onClick={() =>
                              handleAddonChange("decrement", addon)
                            }
                            className="text-zinc-400"
                          >
                            <MinusCircle
                              strokeWidth={1}
                              className="size-4 md:size-5"
                            />
                          </button>
                          <span className="mx-2">
                            {selectedAddons[addon.title] ?? 0}
                          </span>
                          <button
                            onClick={() =>
                              handleAddonChange("increment", addon)
                            }
                            className="text-zinc-400"
                          >
                            <PlusCircle
                              strokeWidth={1}
                              className="size-4 md:size-5"
                            />
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p>No Add On options available from {selectedCity}.</p>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setCurrentForm(currentForm - 1)}
                  className="primary-btn bg-gray-800 disabled:cursor-not-allowed disabled:opacity-55"
                >
                  Step Back
                </button>
                <button
                  disabled={totalPeopleForTransport <= 0}
                  onClick={() => setCurrentForm(currentForm + 1)}
                  className="primary-btn bg-custom-primary disabled:cursor-not-allowed disabled:opacity-55"
                >
                  Next Step
                </button>
              </div>
            </div>

            {/* Total Price */}
            <div className="w-full shrink-0 space-y-8 p-6 lg:p-10">
              <div className="grid gap-4 md:grid-cols-5 lg:gap-5">
                <div className="rounded-lg border border-zinc-400 p-4 text-sm text-secondary-text md:col-span-3">
                  <h3 className="font-lexend text-2xl font-semibold text-black">
                    Your Trip Details
                  </h3>
                  <div className="my-1 flex flex-col justify-between text-sm font-medium md:flex-row md:items-center">
                    <p>
                      City - {selectedCity} to {selectedCity}
                    </p>
                    <p>
                      Trip Date -{" "}
                      {selectedDate?.start_date
                        ? dateFormater({
                            date: selectedDate.start_date,
                            formatString: "dd MMM",
                          })
                        : null}{" "}
                      To{" "}
                      {selectedDate?.end_date
                        ? dateFormater({
                            date: selectedDate.end_date,
                            formatString: "dd MMM",
                          })
                        : null}
                    </p>
                  </div>
                  <div className="my-2 space-y-2">
                    {selectedTransportOptions.length > 0 ? (
                      selectedTransportOptions.map((option, index) => {
                        if (option.people > 0)
                          return (
                            <div
                              key={index}
                              className="flex flex-row justify-between"
                            >
                              <span className="flex flex-nowrap items-center gap-1.5">
                                <span className="h-1 w-1 rounded-full bg-secondary-text/80" />
                                {option.transport}
                              </span>
                              <span className="text-center">
                                {selectedDatePrice
                                  ? `₹${currencyHelper(
                                      selectedDatePrice +
                                        option.price_per_person,
                                    )}/-`
                                  : "please select a date"}{" "}
                                x {option.people} Members
                              </span>
                            </div>
                          );
                      })
                    ) : (
                      <p>No transport options available for {selectedCity}.</p>
                    )}
                  </div>
                  <div className="mx-auto h-0.5 w-5/6 scale-y-[0.33] bg-secondary-text/80" />
                  <div className="my-2 space-y-2">
                    {ParsedAddons.map((addon, index) => {
                      if ((selectedAddons?.[addon.title] ?? 0) > 0)
                        return (
                          <div
                            key={index}
                            className="flex flex-col justify-between md:flex-row"
                          >
                            <span className="flex flex-nowrap items-center gap-1.5">
                              <span className="h-1 w-1 rounded-full bg-secondary-text/80" />
                              {addon.title}
                            </span>
                            <span className="text-center">
                              ₹{currencyHelper(parseInt(addon.price))}
                              /- x {selectedAddons[addon.title] ?? 0} Members
                            </span>
                          </div>
                        );
                    })}
                  </div>
                  <div className="my-1 flex flex-col items-center justify-evenly text-sm font-medium md:flex-row">
                    <p>Total Members - {totalPeopleForTransport} Members</p>
                  </div>
                  <div className="mt-2 space-y-2 rounded-lg border border-zinc-400 p-4 pt-2">
                    <h3 className="font-lexend text-lg font-semibold lg:text-xl">
                      Have a coupon?
                    </h3>
                    <div className="relative flex flex-row gap-4 overflow-hidden rounded-md border border-zinc-400">
                      <input
                        type="text"
                        className="h-10 w-full border-none pl-8 focus-visible:outline-none"
                        placeholder="Enter coupon code"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value)}
                        disabled={couponLoading}
                      />
                      <button
                        className="shrink-0 bg-gray-800 px-5 py-1.5 text-white"
                        onClick={handleCouponFetch}
                        disabled={couponLoading || !couponCode}
                      >
                        {couponLoading ? "Applying..." : "Apply"}
                      </button>
                      <Coupen className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2" />
                    </div>
                    {discount && (
                      <p className="mt-2 text-green-600">
                        Coupon applied! You saved ₹
                        {discount?.discount_amount.toFixed(2)}
                      </p>
                    )}
                  </div>
                  {!user?.phone && (
                    <div className="mt-4">
                      <PhoneInput
                        placeholder="phone number"
                        value={phone ?? ""}
                        onChange={(value) => setPhone(value || undefined)} // Handle value directly
                        defaultCountry="IN"
                        className="peer h-[2.75rem]"
                      />
                    </div>
                  )}
                </div>
                <div className="space-y-4 md:col-span-2">
                  <div className="space-y-2 rounded-lg border border-zinc-400 p-3 text-sm text-secondary-text">
                    <div className="flex flex-1 items-center gap-2">
                      <div className="relative h-16 w-16 shrink-0">
                        <ImageLoader
                          src={img ?? DESTINATION_BG}
                          alt="Trek Karo India"
                          fill
                          className="rounded-lg object-cover"
                        />
                      </div>
                      <div>
                        <h3 className="font-lexend font-bold lg:text-lg">
                          {title}
                        </h3>
                        <div className="flex flex-1 gap-1">
                          <div className="flex flex-nowrap items-center gap-1 text-xs">
                            <MapPin className="size-3" />
                            {selectedCity}
                          </div>
                          <div className="flex flex-nowrap items-center gap-1 text-xs">
                            <Clock className="size-3" />
                            {number_of_days} Days / {number_of_nights} Nights
                          </div>
                        </div>
                      </div>
                    </div>
                    <h3 className="font-lexend text-2xl font-semibold text-black">
                      Price Details
                    </h3>
                    <div className="my-2 space-y-2">
                      {selectedTransportOptions.length > 0 ? (
                        selectedTransportOptions.map((option, index) => {
                          if (option.people > 0)
                            return (
                              <div
                                key={index}
                                className="flex flex-row justify-between font-semibold"
                              >
                                <span className="font-normal">
                                  {selectedDatePrice
                                    ? `₹${
                                        selectedDatePrice +
                                        option.price_per_person
                                      }`
                                    : "please select a date"}{" "}
                                  x {option.people} Members
                                </span>
                                <span>
                                  ₹
                                  {selectedDatePrice
                                    ? (selectedDatePrice +
                                        option.price_per_person) *
                                      option.people
                                    : null}
                                </span>
                              </div>
                            );
                        })
                      ) : (
                        <p>
                          No transport options available for {selectedCity}.
                        </p>
                      )}
                    </div>
                    {addonCost > 0 && (
                      <div className="flex flex-row justify-between font-semibold">
                        <span>Add on Charges</span>
                        <span>₹{currencyHelper(addonCost)}</span>
                      </div>
                    )}
                    <div className="h-0.5 w-full scale-y-[0.33] bg-secondary-text/80" />
                    <div className="flex flex-row justify-between font-semibold">
                      <span>Base Amount</span>
                      <span>₹{totalPrice}</span>
                    </div>
                    {discount && (
                      <>
                        <div className="flex flex-row justify-between font-semibold">
                          <span>Discount</span>
                          <span>₹{discount.discount_amount}</span>
                        </div>
                        <div className="flex flex-row justify-between font-semibold">
                          <span>Subtotal</span>
                          <span>₹{subtotalAfterDiscount}</span>
                        </div>
                      </>
                    )}
                    <div className="flex flex-row justify-between font-semibold">
                      <span>GST(5%)</span>
                      <span>₹{gst_amount}</span>
                    </div>
                    <div className="h-0.5 w-full scale-y-[0.33] bg-secondary-text/80" />
                    <div className="flex flex-row justify-between font-semibold">
                      <span>Grand Total</span>
                      <span>₹{grandTotal}</span>
                    </div>
                    {partialAvailable && (
                      <div className="flex flex-row justify-between font-semibold">
                        <span>Partial Payment</span>
                        <span>₹{formatRupee(amountDueNow)}</span>
                      </div>
                    )}
                  </div>
                  <div className="mt-4 flex items-center gap-2 font-lexend font-semibold">
                    <input
                      type="checkbox"
                      id="terms-conditions"
                      checked={isChecked}
                      onChange={handleCheckboxChange}
                      className="size-4 shrink-0"
                      tabIndex={-1}
                    />
                    <label htmlFor="terms-conditions">
                      I’ve Read and accept the{" "}
                      <Link
                        href={"/terms-and-conditions"}
                        className="text-custom-secondary underline"
                        tabIndex={-1}
                      >
                        terms and conditions.
                      </Link>
                    </label>
                  </div>
                </div>
              </div>
              <div></div>
              {partialAvailable && (
                <p className="text-xs text-gray-500">
                  <strong>Note:</strong> A partial payment is required at the time of booking.
                  The remaining amount must be paid at least 5 days before the
                  scheduled departure date of the batch. Failure to complete the
                  full payment by this deadline may result in cancellation of
                  the booking without a refund of the initial amount.
                </p>
              )}
              {paymentEnabled && enabledPartialPay && paymentQuote && !paymentQuote.partialAvailable && (
                <p className="text-xs text-gray-500">
                  <strong>Note:</strong> Full payment is required because this departure is within the next 5 days.
                </p>
              )}
              {!paymentEnabled && (
                <p className="text-xs text-gray-500">
                  <strong>Note:</strong> Payment is not available for this departure.
                  Your booking will be saved as an enquiry and our team will follow up.
                </p>
              )}
              <div className="flex flex-col-reverse gap-2.5 sm:flex-row items-center justify-between">
                <button
                  onClick={() => setCurrentForm(currentForm - 1)}
                  className="primary-btn cursor-pointer bg-gray-800 disabled:cursor-not-allowed disabled:opacity-55"
                >
                  Step Back
                </button>
                {!paymentEnabled ? (
                  <button
                    disabled={createBookingMutation.isPending || !isChecked}
                    onClick={handleEnquiryBooking}
                    className="primary-btn bg-custom-primary disabled:cursor-not-allowed disabled:opacity-55"
                  >
                    {createBookingMutation.isPending ? "Saving..." : "Continue"}
                  </button>
                ) : (
                <AlertDialog>
                 <div className="flex justify-end gap-3" >
                     {partialAvailable && (
                     <AlertDialogTrigger
                    disabled={checkoutLoading || verifyLoading || !isChecked}
                    className="primary-btn bg-custom-primary disabled:cursor-not-allowed disabled:opacity-55"
                  >
                    <button onClick={()=> setClickOnPartialed(true)} >Partial Payment</button>
                  </AlertDialogTrigger>
                     )}

                  <AlertDialogTrigger
                    asChild
                    disabled={checkoutLoading || verifyLoading || !isChecked}
                    className="primary-btn bg-custom-primary disabled:cursor-not-allowed disabled:opacity-55"
                  >
                    <button onClick={()=> setClickOnPartialed(false)} >Pay Total Amount</button>
                  </AlertDialogTrigger>
                 </div>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        Are you absolutely sure?
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        Please do not refresh or go back until the payment is
                        complete. You will be automatically redirected to your
                        dashboard once the transaction is successfully
                        processed.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        asChild
                        className="primary-btn bg-custom-primary"
                      >
                        <button
                          onClick={handleBooking}
                          className="flex items-start bg-custom-primary"
                        >
                          {checkoutLoading ||
                            (verifyLoading && (
                              <Loader2 className="mr-2 size-5 animate-spin" />
                            ))}{" "}
                          Pay ({formatRupee(clickOnPartialed ? amountDueNow : (paymentQuote?.grandTotal ?? grandTotal))})
                        </button>

                        {/* <button

                                                    onClick={handleBooking}
                                                    className="bg-custom-primary flex items-start"
                                                >
                                                    {checkoutLoading || verifyLoading && <Loader2 className="size-5 mr-2 animate-spin" />} Pay (
                                                    {recieved_amount})
                                                </button> */}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
